import ExecutiveOverview from "@/components/workspace/ExecutiveOverview";
import QuoteBuilder from "@/components/workspace/QuoteBuilder";
import StackEngine from "@/components/workspace/StackEngine";
import CarrierComparison from "@/components/workspace/CarrierComparison";
import FinancialBreakdown from "@/components/workspace/FinancialBreakdown";
import RouteMap from "@/components/workspace/RouteMap";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, FilePlus2 } from "lucide-react";

export default function ClassicWorkspace({ view }) {
  const {
    form, setForm, products, productName, result, financial, selectedRate, selectedCode,
    loading, manualRate, savedQuoteId, savedQuoteNumber, saving, warnings,
    onGetRates, onSelect, onManualRate, onApplyOverride, onClearOverride,
    onSave, onDuplicate, onCustomer, onDiagnostics, onExplain, onNewQuote,
  } = view;

  return (
    <div className="space-y-5 animate-fade-up" data-testid="workspace-layout-classic">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Freight Quote Workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">Pallet stack engineering · density class · FedEx FAK rating · live carrier comparison</p>
        </div>
        <Button data-testid="new-quote-button" variant="outline" onClick={onNewQuote} className="gap-1.5">
          <FilePlus2 className="h-4 w-4" /> New Quote
        </Button>
      </div>

      <ExecutiveOverview form={form} productName={productName} result={result} financial={financial} selectedRate={selectedRate} />

      {warnings.length > 0 && (
        <div className="space-y-1.5" data-testid="warnings-panel">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                w.level === "error"
                  ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                  : w.level === "warning"
                  ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {w.level === "info" ? <Info className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
              {w.message}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <QuoteBuilder form={form} setForm={setForm} products={products} onGetRates={onGetRates} loading={loading} />
        </div>
        <div className="space-y-5 xl:col-span-5">
          <RouteMap originZip={form.origin_zip} destZip={form.destination_zip} pickupDate={form.pickup_date} />
          <StackEngine result={result} />
          <CarrierComparison result={result} selectedCode={selectedCode} onSelect={onSelect} manualRate={manualRate} onManualRate={onManualRate} />
          {manualRate && selectedCode === "MANUAL" && (
            <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              Manual rate entered. Recalculate to refresh comparison & financials.
              <button onClick={onGetRates} className="font-semibold text-[hsl(var(--primary))]">Recalculate</button>
            </div>
          )}
        </div>
        <div className="xl:col-span-3">
          <div className="xl:sticky xl:top-20">
            <FinancialBreakdown
              result={result}
              selectedRate={selectedRate}
              financial={financial}
              onApplyOverride={onApplyOverride}
              onClearOverride={onClearOverride}
              onSave={onSave}
              onDuplicate={onDuplicate}
              onCustomer={onCustomer}
              onDiagnostics={onDiagnostics}
              onExplain={onExplain}
              saving={saving}
              savedQuoteId={savedQuoteId}
              savedQuoteNumber={savedQuoteNumber}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
